/**
 * API Service Layer
 * Centralized service for all backend API communications
 */

import { getAuthToken } from "./clerkToken";

// Use environment variable for API URL, fallback for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL 
  ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
  : "/api/v1";

// Types for API responses
export interface SystemStatus {
  orchestrator_status: string;
  last_analysis: string;
  active_tasks: number;
  completed_today: number;
}

export interface HealthStatus {
  overall_status: string;
  checks: {
    system: {
      status: string;
      checks: {
        cpu: { usage_percent: number };
        memory: { usage_percent: number };
        disk: { usage_percent: number };
      };
    };
    data_freshness: {
      status: string;
      data_sources: {
        [key: string]: {
          status: string;
          last_update: string;
        };
      };
    };
  };
}

export interface AnalysisRequest {
  tickers: string[];
  analysis_type: string;
  config?: Record<string, any>;
  email_notifications?: boolean;
  slack_notifications?: boolean;
}

export interface AnalysisResult {
  status: string;
  execution_time?: number;
  tickers: string[];
  task_id?: string;
  message?: string;
  error?: string;
}

export interface ReportFile {
  filename: string;
  size: number;
  modified: string;
  type: string;
  download_url: string;
}

export interface ReportsResponse {
  reports: ReportFile[];
  count: number;
}

export interface AnalysisHistory {
  history: Array<{
    id: string;
    tickers: string[];
    status: string;
    execution_time: number | null;
    timestamp: string;
    sentiment?: string;
    analysis_type?: string;
  }>;
  count: number;
}

// API Error class
export class APIError extends Error {
  constructor(message: string, public status?: number, public response?: any) {
    super(message);
    this.name = "APIError";
  }
}

// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  const token = await getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const defaultOptions: RequestInit = {
    headers,
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData;

      try {
        errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If response is not JSON, use status text
      }

      throw new APIError(errorMessage, response.status, errorData);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    // Network or other errors
    throw new APIError(
      error instanceof Error ? error.message : "Network error occurred"
    );
  }
}

// API Service functions
export const apiService = {
  // System and Health endpoints
  async getSystemStatus(): Promise<SystemStatus> {
    return apiRequest<SystemStatus>("/status");
  },

  async getHealthStatus(): Promise<HealthStatus> {
    return apiRequest<HealthStatus>("/health");
  },

  async getApiInfo(): Promise<any> {
    return apiRequest<any>("/");
  },

  // Analysis endpoints
  async runAnalysis(request: AnalysisRequest): Promise<AnalysisResult> {
    return apiRequest<AnalysisResult>("/analysis/run", {
      method: "POST",
      body: JSON.stringify(request),
    });
  },

  async getAnalysisHistory(limit: number = 50): Promise<AnalysisHistory> {
    return apiRequest<AnalysisHistory>(`/analysis/history?limit=${limit}`);
  },

  // Reports endpoints
  async getReports(): Promise<ReportsResponse> {
    return apiRequest<ReportsResponse>("/reports");
  },

  async downloadReport(filename: string): Promise<Blob> {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/reports/download/${filename}`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
    if (!response.ok) {
      throw new APIError(`Failed to download report: ${response.statusText}`);
    }
    return response.blob();
  },

  async generateReport(reportData: {
    report_type: string;
    analysis_data: any;
    output_formats: string[];
  }): Promise<any> {
    return apiRequest<any>("/reports/generate", {
      method: "POST",
      body: JSON.stringify(reportData),
    });
  },

  // Market data endpoints
  async getMarketData(tickers: string[], period: string = "1y"): Promise<any> {
    const tickerParams = tickers.map((t) => `tickers=${t}`).join("&");
    return apiRequest<any>(`/market/data?${tickerParams}&period=${period}`);
  },

  // Sentiment analysis endpoint
  async analyzeSentiment(data: {
    text?: string;
    tickers?: string[];
  }): Promise<any> {
    return apiRequest<any>("/sentiment", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Risk assessment endpoint
  async assessRisk(data: {
    tickers: string[];
    weights?: number[];
  }): Promise<any> {
    return apiRequest<any>("/risk/assess", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Scheduler endpoints
  async getScheduledTasks(): Promise<any> {
    return apiRequest<any>("/scheduler/tasks");
  },

  async scheduleAnalysis(data: {
    tickers: string[];
    schedule: string;
    analysis_type: string;
  }): Promise<any> {
    return apiRequest<any>("/scheduler/schedule", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Authentication endpoints
  async getAuthToken(credentials: {
    username: string;
    password: string;
  }): Promise<{ token: string; expires_in: number }> {
    return apiRequest<{ token: string; expires_in: number }>("/auth/token", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  },

  // Settings endpoints
  async getSettings(): Promise<any> {
    return apiRequest<any>("/settings");
  },

  async updateSettings(settings: any): Promise<any> {
    return apiRequest<any>("/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  },

  // User management
  async getUsers(): Promise<{ users: any[]; total?: number; page?: number; limit?: number }> {
    return apiRequest<any>("/users");
  },

  async getUser(userId: string): Promise<any> {
    return apiRequest<any>(`/users/${userId}`);
  },

  async createUser(userData: {
    email: string;
    full_name: string;
    role: string;
    password: string;
  }): Promise<any> {
    return apiRequest<any>("/users", {
      method: "POST",
      body: JSON.stringify({
        ...userData,
        role_name: userData.role,
      }),
    });
  },

  async updateUser(userId: string, userData: any): Promise<any> {
    return apiRequest<any>(`/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  },

  async updateUserRole(userId: string, role: string): Promise<any> {
    return apiRequest<any>(`/users/${userId}/role`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  },

  // Organization management
  async getOrganizations(): Promise<{
    organizations: any[];
    total?: number;
  }> {
    return apiRequest<any>("/organizations");
  },

  async getOrganization(orgId: string): Promise<any> {
    return apiRequest<any>(`/organizations/${orgId}`);
  },

  async updateOrganization(orgId: string, data: any): Promise<any> {
    return apiRequest<any>(`/organizations/${orgId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
};

// Helper function to handle API errors in components
export function handleApiError(error: unknown): string {
  if (error instanceof APIError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
}

// Helper function to check if backend is available
export async function checkBackendHealth(): Promise<boolean> {
  try {
    await apiService.getApiInfo();
    return true;
  } catch {
    return false;
  }
}
